import { Image } from '@rsa-digital/evo-shared-components/commonTypes/image';
import ShareWidgetComponent from '@rsa-digital/evo-shared-components/components/ShareWidget';
import {
  ShareType,
  SharingLink,
} from '@rsa-digital/evo-shared-components/components/ShareWidget/types';
import { graphql, useStaticQuery } from 'gatsby';
import React from 'react';
import { processImageAsset } from 'helpers/csTypeProcessors';
import { nonFatalBuildError } from 'helpers/errorReporting';
import { trackButtonClick } from 'helpers/eventTracking';
import { CsAsset } from 'types/contentStack';

type ShareWidgetProps = {
  social_media_share: {
    title: string | null;
    pinterest_image: { url: string } | null;
    share_types: { share_type: ShareType }[];
  };
  canonical_url?: string;
  page_url?: string;
};

export const query = graphql`
  fragment ShareWidgetBlock on cs__social_media_share {
    title
    share_types {
      share_type
    }
    pinterest_image {
      url
    }
  }
`;

type ShareWidgetLogos = {
  csShareLogos: {
    logos: {
      email: {
        logo: CsAsset;
      };
      facebook: {
        logo: CsAsset;
      };
      linkedin: {
        logo: CsAsset;
      };
      pinterest: {
        logo: CsAsset;
      };
      twitter: {
        logo: CsAsset;
      };
      whatsapp: {
        logo: CsAsset;
      };
    };
  };
};

const shareLogosQuery = graphql`
  query {
    csShareLogos {
      logos {
        email {
          logo {
            ...Asset
          }
        }
        facebook {
          logo {
            ...Asset
          }
        }
        linkedin {
          logo {
            ...Asset
          }
        }
        pinterest {
          logo {
            ...Asset
          }
        }
        twitter {
          logo {
            ...Asset
          }
        }
        whatsapp {
          logo {
            ...Asset
          }
        }
      }
    }
  }
`;

const ShareWidget: React.FC<ShareWidgetProps> = ({
  social_media_share,
  canonical_url,
  page_url,
}) => {
  const { csShareLogos } = useStaticQuery<ShareWidgetLogos>(shareLogosQuery);

  const imageForShareTypes: { [s in ShareType]: Image | undefined } = {
    [ShareType.EMAIL]: processImageAsset(csShareLogos.logos.email.logo),
    [ShareType.FACEBOOK]: processImageAsset(csShareLogos.logos.facebook.logo),
    [ShareType.TWITTER]: processImageAsset(csShareLogos.logos.twitter.logo),
    [ShareType.WHATSAPP]: processImageAsset(csShareLogos.logos.whatsapp.logo),
    [ShareType.LINKEDIN]: processImageAsset(csShareLogos.logos.linkedin.logo),
    [ShareType.PINTEREST]: processImageAsset(csShareLogos.logos.pinterest.logo),
  };

  /* istanbul ignore if */
  if (page_url === undefined) {
    throw new Error('Page url missing for Share Widget');
  }
  const linkUrl = new URL(canonical_url ?? page_url, process.env.GATSBY_SITE_URL).href;

  const link: SharingLink = {
    url: linkUrl,
    title: social_media_share.title ?? undefined,
    description: undefined,
    image: social_media_share.pinterest_image
      ? social_media_share.pinterest_image.url
      : undefined,
  };

  const logos = social_media_share.share_types.map((shareTypeWrapper) => {
    const logoImage = imageForShareTypes[shareTypeWrapper.share_type];
    if (!logoImage) {
      throw new Error(`${shareTypeWrapper.share_type} logo missing!`);
    }

    if (
      shareTypeWrapper.share_type === ShareType.PINTEREST &&
      !social_media_share.pinterest_image
    ) {
      nonFatalBuildError(
        'Image missing',
        'Include Pinterest true and missing Pinterest image for Share Widget'
      );
    }

    return {
      shareType: shareTypeWrapper.share_type,
      image: logoImage,
      onClick: trackButtonClick('shareButton', shareTypeWrapper.share_type),
    };
  });

  return (
    <ShareWidgetComponent
      title={social_media_share.title ?? ''}
      link={link}
      logos={logos}
      data-cy="Share Widget"
    />
  );
};

export default React.memo(ShareWidget);
